import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const VendorReminderListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const routerLocation = useLocation();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  const queryParams = new URLSearchParams(routerLocation.search);
  const initialLocation = queryParams.get('location') || 'all';

  const [rows, setRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [loading, setLoading] = useState<boolean>(false);
  const [locationFilter, setLocationFilter] = useState<string>(initialLocation);
  const [previewRow, setPreviewRow] = useState<any | null>(null);
  const fetchControllerRef = useRef<AbortController | null>(null);

  const updateUrl = (loc: string) => {
    const params = new URLSearchParams();
    if (loc && loc !== 'all') params.set('location', loc);
    const qs = params.toString();
    history.replace(`/vendors/reminders${qs ? `?${qs}` : ''}`);
  };

  const fetchReminders = async (loc: string, pageArg: number, perPageArg: number) => {
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setLoading(true);
    try {
      const params: any = { page: pageArg, per_page: perPageArg };
      if (loc && loc !== 'all') params.location = loc;
      const response: any = await axios.get('/vendors/vendor_reminders', { params, signal: controller.signal });
      setRows(response.data?.result || []);
      setTotalRecords(response.data?.total_records || 0);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch vendor reminders';
      showErrorSnackBar(errorMessage);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handleLocationChange = (newLocation: string) => {
    setLocationFilter(newLocation);
    setPage(1);
    updateUrl(newLocation);
    fetchReminders(newLocation, 1, perPage);
  };

  useEffect(() => {
    fetchReminders(initialLocation, page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: IDataTableColumn<any>[] = [
    {
      name: 'Sent At',
      selector: 'sent_at',
      sortable: false,
      format: (row: any) => formatDateTime(row.sent_at),
    },
    {
      name: 'Vendor',
      selector: 'resolved_vendor_name',
      sortable: false,
      wrap: true,
      format: (row: any) => (
        <Link
          to={`/vendors/invoices/${row.vendor_invoice_id}`}
          style={{ color: 'inherit', textDecoration: 'none' }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {row.resolved_vendor_name || '-'}
        </Link>
      ),
    },
    {
      name: 'Statement #',
      selector: 'statement_number',
      sortable: false,
      wrap: true,
      format: (row: any) => row.statement_number || '-',
    },
    {
      name: 'Location',
      selector: 'location',
      sortable: false,
      format: (row: any) => row.location || '-',
    },
    {
      name: 'Recipients',
      selector: 'recipient_emails',
      sortable: false,
      wrap: true,
      format: (row: any) => row.recipient_emails || '-',
    },
    {
      name: 'Subject',
      selector: 'subject',
      sortable: false,
      wrap: true,
      format: (row: any) => row.subject || '-',
    },
    {
      name: 'Items',
      selector: 'line_items_count',
      sortable: false,
      format: (row: any) => <Chip label={row.line_items_count || 0} size="small" />,
    },
    {
      name: 'Sent By',
      selector: 'sent_by_user_name',
      sortable: false,
      format: (row: any) => row.sent_by_user_name || '-',
    },
  ];

  return (
    <Box style={{ width: '100%' }}>
      <Grid justifyContent="space-between" container spacing={3}>
        <Grid item>
          <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
            Vendor Reminders
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            History of reminder emails that have been sent to vendors.
          </Typography>
        </Grid>
      </Grid>
      <br />
      <Grid container spacing={2} alignItems="flex-start" style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 0.5 }}>
            Location
          </Typography>
          <ToggleButtonGroup
            value={locationFilter}
            exclusive
            onChange={(_e, val) => val && handleLocationChange(val)}
            size="small"
            color="primary"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pittsburgh">Pittsburgh</ToggleButton>
            <ToggleButton value="tampa">Tampa</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <Card>
        <CardContent>
          <DataTable
            title="Reminders"
            columns={columns}
            data={rows}
            onRowClicked={(row) => setPreviewRow(row)}
            striped
            dense
            highlightOnHover
            pointerOnHover
            noHeader
            pagination
            paginationServer
            paginationTotalRows={totalRecords}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[25, 50, 100]}
            onChangePage={(newPage: number) => {
              setPage(newPage);
              fetchReminders(locationFilter, newPage, perPage);
            }}
            onChangeRowsPerPage={(newPerPage: number) => {
              setPerPage(newPerPage);
              setPage(1);
              fetchReminders(locationFilter, 1, newPerPage);
            }}
            progressPending={loading}
          />
        </CardContent>
      </Card>

      <Dialog open={!!previewRow} onClose={() => setPreviewRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>Reminder — {previewRow?.resolved_vendor_name || 'Unknown'}</DialogTitle>
        <DialogContent dividers>
          {previewRow && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Sent At
                  </Typography>
                  <Typography variant="body2">{formatDateTime(previewRow.sent_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Typography variant="body2">{previewRow.status}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Recipients
                  </Typography>
                  <Typography variant="body2">{previewRow.recipient_emails}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Subject
                  </Typography>
                  <Typography variant="body2">{previewRow.subject}</Typography>
                </Grid>
              </Grid>
              <Typography variant="caption" color="textSecondary">
                Body
              </Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  mt: 1,
                  backgroundColor: 'grey.50',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              >
                {previewRow.body}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {previewRow && (
            <Button onClick={() => history.push(`/vendors/invoices/${previewRow.vendor_invoice_id}`)}>
              Open Invoice
            </Button>
          )}
          <Button onClick={() => setPreviewRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorReminderListPage;
